import * as React from "react";
import { TriangleAlert as AlertTriangle, CircleCheck as CheckCircle2, Phone, ShieldCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function Hero() {
  const highlights = [{
    text: "Negative-pressure containment"
  }, {
    text: "Antimicrobial surface treatment"
  }, {
    text: "Independent clearance testing"
  }];
  return <section id="hero" data-nav="dark" className="relative min-h-[640px] lg:min-h-[720px] flex items-center justify-center overflow-hidden bg-foreground text-background">
      {/* Background Image with Responsive Attributes */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/mold_remediation_technician_containment.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/mold_remediation_technician_containment.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/mold_remediation_technician_containment.png-wvc-sizes" alt="TriServe certified restoration technician in full protective gear treating mold inside containment" className="h-full w-full object-cover object-center" loading="eager" decoding="async" />
        {/* Dark Scrim Overlays for Guaranteed Text Legibility */}
        <div className="absolute inset-0 bg-gradient-to-t from-foreground via-foreground/85 to-foreground/75" />
        <div className="absolute inset-0 bg-radial-[at_top_right] from-secondary/40 via-transparent to-transparent opacity-80" />
      </div>

      {/* Main Content Container */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div className="max-w-3xl">
          {/* Operational Status Pill */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-background/10 backdrop-blur-xs border border-background/20 text-xs font-semibold tracking-wide uppercase text-background mb-6">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-accent" />
            </span>
            <span>24/7 Rapid Dispatch &bull; Maryland, DC &amp; Northern VA</span>
          </div>

          {/* Heading */}
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-background leading-[1.1] mb-6">
            Mold remediation
          </h1>

          {/* Supporting Line & Paragraph */}
          <p className="text-lg sm:text-xl font-medium text-background/95 mb-4 leading-relaxed">
            Direct containment, safe removal, and air quality protection for homes and commercial buildings.
          </p>

          <p className="text-base text-background/80 mb-8 max-w-2xl leading-relaxed">
            Our local teams set up negative-pressure HEPA barriers to isolate active mold spores, strip damaged drywall and framing safely, apply hospital-grade antimicrobial solutions, and manage post-remediation clearance testing. You speak directly with our crew leaders and work with your insurance adjusters from start to finish.
          </p>

          {/* Key Checklist Badges */}
          <div className="flex flex-wrap gap-y-2 gap-x-4 mb-8 text-sm text-background/90">
            {highlights.map((item, i) => <div key={item.text} data-index={i} className="flex items-center gap-2 bg-background/5 border border-background/15 rounded-md px-3 py-1.5">
                <CheckCircle2 className="size-4 text-accent shrink-0" />
                <span>{item.text}</span>
              </div>)}
          </div>

          {/* Call-to-Action Buttons */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold text-base px-7 py-3.5 shadow-md transition-all">
              <a href="tel:855-435-6631">
                <Phone className="size-5 mr-2 shrink-0" />
                Call Now
              </a>
            </Button>

            <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 border border-accent font-extrabold text-base px-7 py-3.5 shadow-md transition-all">
              <Link to="/ContactRequestServicePage">
                <ShieldCheck className="size-5 mr-2 shrink-0" />
                Request Service
              </Link>
            </Button>
          </div>

          {/* Dispatch Notice Strip */}
          <div className="mt-8 pt-6 border-t border-background/15 flex flex-wrap items-center gap-y-2 gap-x-6 text-xs text-background/75 font-mono">
            <div className="flex items-center gap-1.5">
              <AlertTriangle className="size-3.5 text-accent shrink-0" />
              <span>No Call Center Dispatch</span>
            </div>
            <div>Direct Insurance Billing</div>
            <div>IICRC Certified Remediation</div>
          </div>
        </div>
      </div>
    </section>;
}